import { g } from "analytics";
import { apparatusClass } from "apparatus/library";
import { LIQUID_OPACITY_RANGE } from "apparatus/library/common";
import { UpdateLiquidAlpha, UpdateLiquidAmount, UpdateLiquidColor } from "command/basic";
import { commandProcessor } from "command/command";
import { Button } from "components/button";
import { Icon } from "components/icon";
import { MultiLiquid, UpdateLiquidLayers } from "editing/liquid/multilayer";
import { isLiquidLevelSufficientForDraining, isWithinDrainingAngles, isWithinPouringAngles, LIQUID_DRAINING_MAX_ROTATION_ANGLE, LIQUID_DRAINING_MIN_LIQUID_LEVEL, LIQUID_POURING_MAX_ROTATION_ANGLE, LIQUID_POURING_MIN_ROTATION_ANGLE, UpdateLiquidDrainingEnabled, UpdateLiquidDrainingFade, UpdateLiquidDrainingFlowLength, UpdateLiquidDrainingThinning, UpdateLiquidMeniscusConvex, UpdateLiquidMeniscusEnabled, UpdateLiquidMeniscusRadius, UpdateLiquidPouringEnabled, UpdateLiquidPouringFade, UpdateLiquidPouringFlowLength, UpdateLiquidPouringFlowStrength } from "editing/liquid/surface";
import { t } from "i18next";
import * as React from "react";
import { Articles } from "support/beacon";
const MAX_TOTAL_EXTRA_LAYERS = 4;
const MAX_EXTRA_LAYERS_FOR_FREE_TIER = 1;
export function createSingleLayerLiquidPropsSections(items, app, meniscus) {
    const liquidData = items[0].liquid; // Take liquid data of first item.
    let elements = [
        {
            spec: "slider", label: "Amount",
            min: 0, max: 1, step: 0.001, initialValue: liquidData.amountRatio,
            key: "liquid_amount", unit: "%",
            onChange: value => {
                for (const item of items) {
                    const component = app.getApparatusComponent(item.id);
                    if (!component || !component.state.liquid)
                        continue;
                    component === null || component === void 0 ? void 0 : component.updateLiquidData(Object.assign(Object.assign({}, component.state.liquid), { amountRatio: value }));
                }
            },
            onEnd: (previous, final) => {
                g("UpdateAppearance", `${items[0].type}::liquid::amount`);
                let command = new UpdateLiquidAmount(items.map(i => ({
                    id: i.id,
                    value: final,
                    previousValue: i.liquid.amountRatio
                })));
                commandProcessor.execute(command);
            }
        },
    ];
    // The next section can only be added if there is a single item, or if multiple items all
    // don't have extra layers.
    if (items.length == 1 || items.every(i => { var _a; return ((_a = i.liquid) === null || _a === void 0 ? void 0 : _a.layers.length) == 0; })) {
        elements.push({
            spec: "color", label: "Colour",
            key: "liquid_color" + "_" + items[0].id.id,
            value: liquidData.color,
            onColorChange: value => {
                for (const item of items) {
                    let component = app.getApparatusComponent(item.id);
                    if (!component || !item.liquid)
                        continue;
                    component.updateLiquidData(Object.assign(Object.assign({}, item.liquid), { color: value }));
                }
            },
            onColorEnd: (final) => {
                if (final == items[0].liquid.color)
                    return;
                g("UpdateAppearance", `${items[0].type}::liquid::colour`);
                let command = new UpdateLiquidColor(items.filter(i => i.liquid !== undefined).map(i => ({
                    id: i.id,
                    value: final,
                    previousValue: i.liquid.color
                })));
                commandProcessor.execute(command);
            },
            opacity: {
                value: liquidData.alpha,
                range: LIQUID_OPACITY_RANGE,
                onChange: value => {
                    for (const item of items) {
                        const component = app.getApparatusComponent(item.id);
                        if (!component || !component.state.liquid)
                            continue;
                        component.updateLiquidData(Object.assign(Object.assign({}, component.state.liquid), { alpha: value }));
                    }
                },
                onEnd: (_, final) => {
                    g("UpdateAppearance", `${items[0].type}::liquid::alpha`);
                    let command = new UpdateLiquidAlpha(items.map(i => ({
                        id: i.id,
                        value: final,
                        previousValue: i.liquid.alpha
                    })));
                    commandProcessor.execute(command);
                }
            }
        });
    }
    let pourElements = [];
    const supportsPouring = items.every(i => apparatusClass(i.type).properties.pourable);
    if (supportsPouring && liquidData.pouring.enabled) {
        pourElements.push({
            spec: "slider", label: "Flow length", key: "flow-size", unit: "mm",
            min: 10, max: 400, initialValue: liquidData.pouring.flowLength,
            onChange: value => {
                for (const item of items) {
                    const component = app.getApparatusComponent(item.id);
                    if (!component || !component.state.liquid)
                        continue;
                    component.updateLiquidData(Object.assign(Object.assign({}, component.state.liquid), { pouring: Object.assign(Object.assign({}, component.state.liquid.pouring), { flowLength: value }) }));
                }
            },
            onEnd: (_, final) => {
                commandProcessor.execute(new UpdateLiquidPouringFlowLength(items.map(i => ({
                    id: i.id, value: final, previousValue: i.liquid.pouring.flowLength,
                }))));
            }
        }, {
            spec: "slider", label: "Flow strength", key: "flow-size", unit: "%",
            min: 0, max: 1, step: 0.01, initialValue: liquidData.pouring.flowStrength,
            onChange: value => {
                for (const item of items) {
                    const component = app.getApparatusComponent(item.id);
                    if (!component || !component.state.liquid)
                        continue;
                    component.updateLiquidData(Object.assign(Object.assign({}, component.state.liquid), { pouring: Object.assign(Object.assign({}, component.state.liquid.pouring), { flowStrength: value }) }));
                }
            },
            onEnd: (_, final) => {
                commandProcessor.execute(new UpdateLiquidPouringFlowStrength(items.map(i => ({
                    id: i.id, value: final, previousValue: i.liquid.pouring.flowStrength,
                }))));
            }
        }, {
            spec: "checkbox-group", label: "Fading", key: "pouring-fading",
            checkboxes: [
                {
                    label: "", value: liquidData.pouring.fade, id: "pouring-fading",
                    onCheckedChange: value => {
                        commandProcessor.execute(new UpdateLiquidPouringFade(items.map(i => ({
                            id: i.id, value, previousValue: i.liquid.pouring.fade,
                        }))));
                    }
                }
            ]
        });
        // Show warning if set up is incorrect.
        if (!isWithinPouringAngles(items[0].rotation)) {
            pourElements.push(React.createElement(WarningHint, { key: "pour-warn", text: t('info.pour_warn_angle', { angle1: LIQUID_POURING_MIN_ROTATION_ANGLE, angle2: LIQUID_POURING_MAX_ROTATION_ANGLE }) }));
        }
        // Show warning if multiple layers are used.
        if (liquidData.layers.length > 0) {
            pourElements.push(React.createElement(WarningHint, { key: "pour-warn-2", text: t('info.pour_warn_layers') }));
        }
    }
    let meniscusElements = [];
    if (meniscus && liquidData.meniscus.enabled) {
        meniscusElements.push({
            spec: "toggle-horizontal-buttons", label: "Type", key: "meniscus-type",
            size: "small", highlightIndex: liquidData.meniscus.convex ? 1 : 0,
            buttons: [
                {
                    buttonLabel: "Concave", icon: "k,meniscus-concave", onClick: () => {
                        commandProcessor.execute(new UpdateLiquidMeniscusConvex(items.map(i => ({
                            id: i.id, value: false, previousValue: i.liquid.meniscus.convex,
                        }))));
                    }
                },
                {
                    buttonLabel: "Convex", icon: "k,meniscus-convex", onClick: () => {
                        commandProcessor.execute(new UpdateLiquidMeniscusConvex(items.map(i => ({
                            id: i.id, value: true, previousValue: i.liquid.meniscus.convex,
                        }))));
                    }
                },
            ]
        }, {
            spec: "slider", label: "Radius", key: "meniscus-radius", unit: "mm",
            min: 2, max: 20, step: 1, initialValue: liquidData.meniscus.radius,
            onChange: radius => {
                for (const item of items) {
                    const component = app.getApparatusComponent(item.id);
                    if (!component || !component.state.liquid)
                        continue;
                    component.updateLiquidData(Object.assign(Object.assign({}, component.state.liquid), { meniscus: Object.assign(Object.assign({}, component.state.liquid.meniscus), { radius }) }));
                }
            },
            onEnd: (_, final) => {
                commandProcessor.execute(new UpdateLiquidMeniscusRadius(items.map(i => ({
                    id: i.id, value: final, previousValue: i.liquid.meniscus.radius,
                }))));
            }
        });
        // Show warning if set up is incorrect.
        if (items[0].rotation != 0) {
            meniscusElements.push(React.createElement(WarningHint, { key: "meniscus-warn", text: t("info.meniscus_hint") }));
        }
    }
    let drainingElements = [];
    const supportsDraining = items.every(i => apparatusClass(i.type).properties.drainable);
    if (supportsDraining && liquidData.draining.enabled) {
        drainingElements.push({
            spec: "slider", label: "Flow length", key: "drain-flow-length", unit: "mm",
            min: 20, max: 250, initialValue: liquidData.draining.flowLength,
            onChange: value => {
                for (const item of items) {
                    const component = app.getApparatusComponent(item.id);
                    if (!component || !component.state.liquid)
                        continue;
                    component.updateLiquidData(Object.assign(Object.assign({}, component.state.liquid), { draining: Object.assign(Object.assign({}, component.state.liquid.draining), { flowLength: value }) }));
                }
            },
            onEnd: (_, final) => {
                commandProcessor.execute(new UpdateLiquidDrainingFlowLength(items.map(i => ({
                    id: i.id, value: final, previousValue: i.liquid.draining.flowLength,
                }))));
            }
        }, {
            spec: "checkbox-group", label: "Options", key: "draining-fading",
            checkboxes: [
                {
                    label: "Fade", value: liquidData.draining.fade, id: "draining-fading",
                    onCheckedChange: value => {
                        commandProcessor.execute(new UpdateLiquidDrainingFade(items.map(i => ({
                            id: i.id, value, previousValue: i.liquid.draining.fade,
                        }))));
                    }
                },
                {
                    label: "Thinning", value: liquidData.draining.thinning, id: "draining-thinning",
                    onCheckedChange: value => {
                        commandProcessor.execute(new UpdateLiquidDrainingThinning(items.map(i => ({
                            id: i.id, value, previousValue: i.liquid.draining.thinning,
                        }))));
                    }
                }
            ]
        });
        // Show warning if set up is incorrect.
        if (!isWithinDrainingAngles(items[0].rotation)) {
            drainingElements.push(React.createElement(WarningHint, { key: "meniscus-warn", text: `The rotation must be less than ${LIQUID_DRAINING_MAX_ROTATION_ANGLE}°.` }));
        }
        if (!isLiquidLevelSufficientForDraining(items[0].liquid)) {
            drainingElements.push(React.createElement(WarningHint, { key: "meniscus-warn", text: `Set the liquid amount to at least ${Math.round(LIQUID_DRAINING_MIN_LIQUID_LEVEL * 100)}%.` }));
        }
    }
    return [
        {
            hideHeaderIfFirst: true,
            icon: "tint",
            header: "Liquid",
            elements,
        },
        // Pouring section.
        {
            icon: "fill-drip",
            header: "Pouring",
            elements: pourElements,
            helpArticleId: Articles.POURING,
            toggle: supportsPouring ? {
                id: "pouring-enabled",
                value: liquidData.pouring.enabled,
                onCheckedChange: value => {
                    commandProcessor.execute(new UpdateLiquidPouringEnabled(items.map(i => ({
                        id: i.id, value, previousValue: i.liquid.pouring.enabled,
                    }))));
                }
            } : undefined
        },
        // Draining section.
        {
            header: "Draining",
            icon: "filter",
            elements: drainingElements,
            toggle: supportsDraining ? {
                id: "draining-enabled",
                value: liquidData.draining.enabled,
                onCheckedChange: value => {
                    commandProcessor.execute(new UpdateLiquidDrainingEnabled(items.map(i => ({
                        id: i.id, value, previousValue: i.liquid.draining.enabled,
                    }))));
                }
            } : undefined
        },
        // Meniscus.
        {
            icon: "k,meniscus-concave",
            header: "Meniscus",
            elements: meniscusElements,
            helpArticleId: Articles.MENISCUS,
            toggle: meniscus ? {
                id: "meniscus-enabled",
                value: liquidData.meniscus.enabled,
                onCheckedChange: value => {
                    commandProcessor.execute(new UpdateLiquidMeniscusEnabled(items.map(i => ({
                        id: i.id, value, previousValue: i.liquid.meniscus.enabled,
                    }))));
                }
            } : undefined
        }
    ];
}
export function createLiquidPropsSection(item, liquidData, app, meniscus, tiers) {
    let elements = [];
    // Present the layers in reverse order, so that it visually matches
    // with how the layers are rendered.
    for (let i = liquidData.layers.length - 1; i >= 0; i--) {
        const layer = liquidData.layers[i];
        const index = i;
        elements.push(React.createElement("hr", { key: "liquid_hr_" + i }), React.createElement("h4", { key: "liquid_label_" + i }, t("ui.layer_n", { count: liquidData.layers.length - i })));
        elements.push({
            spec: "slider", label: "Position",
            min: 0, max: 1, step: 0.01, initialValue: layer.position,
            key: "liquid_position_" + i, unit: "%",
            onChange: value => {
                // CALLBACK! Do NOT refer to "i".
                let component = app.getApparatusComponent(item.id);
                if (!component || !component.state.liquid)
                    return;
                const newLayers = MultiLiquid.adjustNumeric(liquidData.layers, index, "position", value);
                component.updateLiquidData(Object.assign(Object.assign({}, component.state.liquid), { layers: newLayers }));
            },
            onEnd: (previous, final) => {
                if (previous == final)
                    return;
                // CALLBACK! Do NOT refer to "i".
                const newLayers = MultiLiquid.adjustNumeric(liquidData.layers, index, "position", final);
                commandProcessor.execute(new UpdateLiquidLayers(item.id, newLayers, liquidData.layers));
            }
        }, {
            spec: "color", label: "Colour",
            key: "liquid_color_" + item.id.id + "_" + i,
            value: layer.color,
            onColorChange: value => {
                // CALLBACK! Do NOT refer to "i".
                let component = app.getApparatusComponent(item.id);
                if (!component || !component.state.liquid)
                    return;
                const newLayers = MultiLiquid.adjustString(liquidData.layers, index, "color", value);
                component.updateLiquidData(Object.assign(Object.assign({}, component.state.liquid), { layers: newLayers }));
            },
            onColorEnd: (final) => {
                // Careful here! We need to compare lowercase because the output
                // will be uppercase from the bootstrap color picker! Also
                // onColorEnd is called when the component is unmounted, so it's
                // possible that this layer no longer exists. Be careful!
                if (final.toLowerCase() == layer.color.toLowerCase())
                    return;
                // CALLBACK! Do NOT refer to "i".
                const newLayers = MultiLiquid.adjustString(liquidData.layers, index, "color", final);
                commandProcessor.execute(new UpdateLiquidLayers(item.id, newLayers, liquidData.layers));
            },
            opacity: {
                value: layer.alpha,
                range: LIQUID_OPACITY_RANGE,
                onChange: value => {
                    // CALLBACK! Do NOT refer to "i".
                    let component = app.getApparatusComponent(item.id);
                    if (!component || !component.state.liquid)
                        return;
                    const newLayers = MultiLiquid.adjustNumeric(liquidData.layers, index, "alpha", value);
                    component.updateLiquidData(Object.assign(Object.assign({}, component.state.liquid), { layers: newLayers }));
                },
                onEnd: (previous, final) => {
                    if (previous == final)
                        return;
                    // CALLBACK! Do NOT refer to "i".
                    const newLayers = MultiLiquid.adjustNumeric(liquidData.layers, index, "alpha", final);
                    commandProcessor.execute(new UpdateLiquidLayers(item.id, newLayers, liquidData.layers));
                }
            }
        }, {
            spec: "slider", label: "Blending",
            min: 0, max: 1, step: 0.01, initialValue: layer.blending,
            key: "liquid_blending_" + i, unit: "%",
            onChange: value => {
                // CALLBACK! Do NOT refer to "i".
                let component = app.getApparatusComponent(item.id);
                if (!component || !component.state.liquid)
                    return;
                const newLayers = MultiLiquid.adjustNumeric(liquidData.layers, index, "blending", value);
                component.updateLiquidData(Object.assign(Object.assign({}, component.state.liquid), { layers: newLayers }));
            },
            onEnd: (previous, final) => {
                if (previous == final)
                    return;
                // CALLBACK! Do NOT refer to "i".
                const newLayers = MultiLiquid.adjustNumeric(liquidData.layers, index, "blending", final);
                commandProcessor.execute(new UpdateLiquidLayers(item.id, newLayers, liquidData.layers));
            }
        });
    }
    let buttons = [];
    const currentExtraLayers = liquidData.layers.length;
    if (currentExtraLayers < MAX_TOTAL_EXTRA_LAYERS) {
        buttons.push(React.createElement(Button, { buttonLabel: "Add layer", icon: "plus", key: "addLayer", tier: currentExtraLayers < MAX_EXTRA_LAYERS_FOR_FREE_TIER ? undefined : "boost", onClick: () => {
                g("UpdateAppearance", `${item.type}::liquid::add_layer`);
                const newLayers = MultiLiquid.addLayer(liquidData.layers);
                commandProcessor.execute(new UpdateLiquidLayers(item.id, newLayers, liquidData.layers));
            } }));
    }
    if (liquidData.layers.length === 0) {
        buttons.push(React.createElement(Button, { buttonLabel: "Quick blend", icon: "fill-drip", key: "quick_blend", onClick: () => {
                g("UpdateAppearance", `${item.type}::liquid::quick_blend`);
                const newLayers = MultiLiquid.createSimpleGradient(liquidData.color, liquidData.alpha);
                commandProcessor.execute(new UpdateLiquidLayers(item.id, newLayers, liquidData.layers));
            } }));
    }
    if (liquidData.layers.length > 0) {
        buttons.push(React.createElement(Button, { buttonLabel: "Remove layer", icon: "minus", key: "removeLayer", onClick: () => {
                g("UpdateAppearance", `${item.type}::liquid::remove_layer`);
                const newLayers = MultiLiquid.removeBottomLayer(liquidData.layers);
                commandProcessor.execute(new UpdateLiquidLayers(item.id, newLayers, liquidData.layers));
            } }));
    }
    if (buttons.length > 0) {
        elements.push(React.createElement("div", { className: "flex-group", key: "liquidLayerButtons" },
            React.createElement("div", { className: "label" }),
            React.createElement("div", { className: "verticalButtonGroup mt-2", key: "liquidLayerButtons" }, buttons)));
    }
    // Attach elements to the first section.
    const panels = createSingleLayerLiquidPropsSections([item], app, meniscus);
    panels[0].elements.push(...elements);
    return panels;
}
const WarningHint = (props) => React.createElement("div", { className: "boxed-hint border-warning text-warning d-flex" },
    React.createElement(Icon, { name: "exclamation-triangle", extraClass: "mr-2 mt-1" }),
    props.text);
